import React, { useEffect, useMemo, useState } from "react";

import Button from "../../componentes/button/button";
import Input from "../../componentes/input/input";

import DespesaService from "../../services/resource/DespesaService";
import ReceitaService from "../../services/resource/ReceitaService";

import { toast } from "react-toastify";

const DEFAULT_COLS = {
    filial: 0,
    doc: 1,
    data: 2,
    conta: 4,
    historico: 6,
    valor: 7,
    centroCusto: 8,
};

const TIPOS_IMPORTACAO = {
    DESPESA: {
        key: "DESPESA",
        titulo: "Despesas",
        icon: "fa fa-arrow-trend-down",
        activeClass: "border-destructive/40 bg-destructive/10 text-destructive",
        iconClass: "bg-destructive/15 text-destructive",
    },

    RECEITA: {
        key: "RECEITA",
        titulo: "Receitas",
        icon: "fa fa-arrow-trend-up",
        activeClass: "border-success/40 bg-success/10 text-success",
        iconClass: "bg-success/15 text-success",
    },
};

export default function ImportadorFinanceiroExcel() {

    const despesaService = useMemo(() => new DespesaService(), []);
    const receitaService = useMemo(() => new ReceitaService(), []);

    const [step, setStep] = useState(1);

    const [tipoImportacao, setTipoImportacao] = useState("DESPESA");

    const [file, setFile] = useState(null);

    const [loading, setLoading] = useState(false);

    const [resultado, setResultado] = useState(null);

    const [showConfig, setShowConfig] = useState(false);

    const [cols, setCols] = useState(DEFAULT_COLS);

    useEffect(() => {

        const saved = localStorage.getItem(
            `mapeamento-colunas-${tipoImportacao}`
        );

        if (saved) {
            setCols(JSON.parse(saved));
        }

    }, [tipoImportacao]);

    useEffect(() => {

        localStorage.setItem(
            `mapeamento-colunas-${tipoImportacao}`,
            JSON.stringify(cols)
        );

    }, [cols, tipoImportacao]);

    const getService = () => {

        if (tipoImportacao === "DESPESA") {
            return despesaService;
        }

        return receitaService;
    };

    const handleChangeCol = (e) => {

        const { name, value } = e.target;

        setCols((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleImport = async () => {

        if (!file) {
            toast.warning("Selecione uma planilha.");
            return;
        }

        try {

            setLoading(true);

            setResultado(null);

            const service = getService();

            const response = await service.importarExcel({
                file,
                filial: Number(cols.filial),
                data: Number(cols.data),
                conta: Number(cols.conta),
                historico: Number(cols.historico),
                valor: Number(cols.valor),
                centroCusto: Number(cols.centroCusto),
                doc: Number(cols.doc),
            });

            setResultado({
                sucesso: true,
                mensagem: response.data,
            });

            setStep(3);

            toast.success("Importação concluída!");

        } catch (error) {

            setResultado({
                sucesso: false,
                mensagem:
                    error.response?.data ||
                    "Erro ao processar importação.",
            });

            setStep(3);

            toast.error("Erro ao importar planilha.");

        } finally {

            setLoading(false);
        }
    };

    return (
        <div className="space-y-5 py-2">

            {/* STEPS */}
            <div className="flex items-center justify-center gap-3">

                {[1, 2, 3].map((item) => (

                    <div
                        key={item}
                        className="flex items-center gap-3"
                    >

                        <div
                            className={`
                                h-9 w-9 rounded-full
                                flex items-center justify-center
                                text-sm font-semibold
                                transition-colors duration-150

                                ${step >= item
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted text-muted-foreground"}
                            `}
                        >
                            {item}
                        </div>

                        {item < 3 && (
                            <div className="
                                w-10 h-[2px] bg-border
                            " />
                        )}

                    </div>
                ))}

            </div>

            {/* STEP 1 */}
            {step === 1 && (

                <div className="space-y-4">

                    <div>

                        <h2 className="
                            font-display text-lg font-semibold text-foreground
                        ">
                            Tipo da Importação
                        </h2>

                        <p className="
                            text-sm text-muted-foreground mt-1
                        ">
                            Escolha o tipo de lançamento.
                        </p>

                    </div>

                    <div className="grid grid-cols-2 gap-3">

                        {Object.values(TIPOS_IMPORTACAO).map((tipo) => {

                            const active =
                                tipoImportacao === tipo.key;

                            return (
                                <button
                                    key={tipo.key}
                                    type="button"
                                    onClick={() => setTipoImportacao(tipo.key)}
                                    className={`
                                        border rounded-2xl p-4
                                        transition-all text-left
                                        
                                        ${active
                                            ? tipo.activeClass
                                            : "border-gray-200 bg-white hover:border-gray-300"}
                                    `}
                                >

                                    <div className="flex items-center gap-3">

                                        <div
                                            className={`
                                                h-10 w-10 rounded-xl
                                                flex items-center justify-center
                                                ${tipo.iconClass}
                                            `}
                                        >
                                            <i className={tipo.icon}></i>
                                        </div>

                                        <div>

                                            <div className="
                                                font-medium
                                            ">
                                                {tipo.titulo}
                                            </div>

                                            <div className="
                                                text-xs opacity-70 mt-1
                                            ">
                                                Importação financeira
                                            </div>

                                        </div>

                                    </div>

                                </button>
                            );
                        })}

                    </div>

                    <div className="flex justify-end">

                        <Button
                            label="Continuar"
                            iconRight="fa fa-arrow-right"
                            onClick={() => setStep(2)}
                        />

                    </div>

                </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (

                <div className="space-y-4">

                    <div>

                        <h2 className="
                            font-display text-lg font-semibold text-foreground
                        ">
                            Upload da Planilha
                        </h2>

                        <p className="
                            text-sm text-muted-foreground mt-1
                        ">
                            Selecione a planilha Excel.
                        </p>

                    </div>

                    {/* UPLOAD */}
                    <label
                        className="
                            border-2 border-dashed border-gray-300
                            rounded-2xl
                            p-6
                            flex flex-col items-center justify-center
                            text-center
                            cursor-pointer
                            hover:border-blue-400
                            hover:bg-blue-50
                            transition-all
                        "
                    >

                        <i className="
                            fa fa-file-excel
                            text-4xl text-green-600 mb-3
                        "></i>

                        <div className="
                            text-sm font-medium text-foreground
                        ">
                            Clique para selecionar a planilha
                        </div>

                        <div className="
                            text-xs text-muted-foreground mt-1
                        ">
                            .xls ou .xlsx
                        </div>

                        {file && (
                            <div className="
                                mt-4
                                bg-blue-100 text-blue-700
                                px-3 py-1 rounded-full
                                text-xs font-medium
                            ">
                                {file.name}
                            </div>
                        )}

                        <input
                            type="file"
                            accept=".xls,.xlsx"
                            className="hidden"
                            onChange={(e) => {
                                setFile(e.target.files[0]);
                            }}
                        />

                    </label>

                    {/* CONFIG */}
                    <div className="
                        border rounded-2xl overflow-hidden
                    ">

                        <button
                            type="button"
                            onClick={() => setShowConfig(!showConfig)}
                            className="
                                w-full flex items-center justify-between
                                px-4 py-3
                                bg-gray-50 hover:bg-gray-100
                                transition-all
                            "
                        >

                            <div className="
                                flex items-center gap-2
                                text-sm font-medium text-foreground
                            ">
                                <i className="fa fa-gear"></i>
                                Configuração avançada
                            </div>

                            <i
                                className={`
                                    fa ${showConfig
                                        ? "fa-chevron-up"
                                        : "fa-chevron-down"}
                                    text-xs text-gray-400
                                `}
                            ></i>

                        </button>

                        {showConfig && (

                            <div className="p-4 space-y-3 bg-white">

                                {[
                                    ["filial", "Filial"],
                                    ["doc", "Documento"],
                                    ["data", "Data"],
                                    ["conta", "Conta"],
                                    ["historico", "Histórico"],
                                    ["valor", "Valor"],
                                    ["centroCusto", "Centro Custo"],
                                ].map(([name, label]) => (

                                    <div
                                        key={name}
                                        className="
                                            flex items-center justify-between
                                            gap-3
                                        "
                                    >

                                        <span className="
                                            text-sm text-muted-foreground
                                        ">
                                            {label}
                                        </span>

                                        <div className="w-20">

                                            <Input
                                                type="number"
                                                name={name}
                                                value={cols[name]}
                                                onChange={handleChangeCol}
                                            />

                                        </div>

                                    </div>
                                ))}

                            </div>
                        )}

                    </div>

                    {/* ACTIONS */}
                    <div className="
                        flex items-center justify-between
                    ">

                        <Button
                            outlined
                            label="Voltar"
                            iconLeft="fa fa-arrow-left"
                            onClick={() => setStep(1)}
                        />

                        <Button
                            label={
                                loading
                                    ? "Importando..."
                                    : "Importar"
                            }
                            iconLeft="fa fa-upload"
                            disabled={!file || loading}
                            loading={loading}
                            onClick={handleImport}
                        />

                    </div>

                </div>
            )}

            {/* STEP 3 */}
            {step === 3 && resultado && (

                <div className="space-y-4">

                    <div
                        className={`
                            rounded-2xl border p-5
                            
                            ${resultado.sucesso
                                ? "bg-green-50 border-green-200"
                                : "bg-red-50 border-red-200"}
                        `}
                    >

                        <div className="
                            flex items-start gap-4
                        ">

                            <div
                                className={`
                                    h-12 w-12 rounded-2xl
                                    flex items-center justify-center
                                    text-xl
                                    
                                    ${resultado.sucesso
                                        ? "bg-green-100 text-green-600"
                                        : "bg-red-100 text-red-600"}
                                `}
                            >

                                <i
                                    className={
                                        resultado.sucesso
                                            ? "fa fa-check"
                                            : "fa fa-xmark"
                                    }
                                ></i>

                            </div>

                            <div className="flex-1 max-h-[250px] overflow-y-auto">

                                <h3 className="
                                    font-display text-lg font-semibold text-foreground
                                ">
                                    {resultado.sucesso
                                        ? "Importação concluída"
                                        : "Erro na importação"}
                                </h3>

                                <div
                                    className="
                                        text-sm whitespace-pre-wrap
                                        text-foreground mt-2
                                    "
                                >
                                    {resultado.mensagem}
                                </div>

                            </div>

                        </div>

                    </div>

                    <div className="flex justify-between">

                        <Button
                            outlined
                            label="Nova Importação"
                            iconLeft="fa fa-rotate"
                            onClick={() => {

                                setResultado(null);

                                setFile(null);

                                setStep(1);
                            }}
                        />

                    </div>

                </div>
            )}

        </div>
    );
}